<template>
  <div class="admin-page content">
    <!-- ───────────── Titre & sous-barre ───────────── -->
    <div class="title-bar">
      <h2 class="page-title">Console d'administration - {{ cabinet_name }}</h2>
    </div>

    <!-- 1️⃣  Courbe d'utilisation -->
    <div class="card chart-card mb-5">
      <div class="card-header">
        <h5 class="card-title">Requêtes sur 7 jours</h5>
      </div>
      <div class="card-body">
        <div class="chart-wrapper">
          <canvas ref="usageChart"></canvas>
        </div>
      </div>
    </div>

    <!-- 2️⃣  Tableau des utilisateurs (triés par nombre de requêtes) -->
    <div class="card table-card">
      <div class="table-responsive">
        <table class="table table-hover table-sm mb-0">
          <thead>
            <tr>
              <th>Email</th>
              <th class="text-right">
                Requêtes (30 j)
                <i class="fas fa-sort-amount-down ml-1 text-muted"></i>
              </th>
              <th class="text-right">Dernière requête</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in sortedUsers" :key="u.id">
              <td>
                <span class="user-email">{{ u.email }}</span>
                <span v-if="u.id === me.id" class="badge badge-info ml-1">vous</span>
                <span v-else-if="u.is_cab_admin" class="badge badge-primary ml-1">admin</span>
              </td>
              <td class="text-right">{{ u.queries }}</td>
              <td class="text-right">{{ u.lastRequestDate ? formatDate(u.lastRequestDate) : '-' }}</td>
            </tr>
            <tr v-if="!sortedUsers.length">
              <td colspan="3" class="text-center text-muted py-4">Aucun membre pour l'instant</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/supabase/supabaseClient';
import Chart from 'chart.js/auto';
import { getOwnProfile } from '@/supabase/profile';

export default {
  name: 'AdminConsolePage',
  data () {
    return {
      me    : null,
      users : [],      // [{id,email,is_cab_admin,queries,lastRequestDate}]
      chart : null,
      cabinet_name: 'Chargement...' // Valeur par défaut pendant le chargement
    };
  },
  computed: {
    // Utilisateurs triés par nombre de requêtes (décroissant)
    sortedUsers() {
      return [...this.users].sort((a, b) => (b.queries || 0) - (a.queries || 0));
    }
  },
  async mounted () {
    /* 0️⃣  profil + vérification */
    this.me = await getOwnProfile();
    if (!this.me?.is_cab_admin || !this.me?.admin_view_enabled) return this.$router.replace({ name: 'dashboard' });

    /* 0️⃣.1 récupération des infos du cabinet */
    await this.fetchCabinetDetails();

    /* 1️⃣  Courbe d'utilisation */
    await this.fetchChart();

    /* 2️⃣  Tableau utilisateurs */
    await this.fetchUsers();
  },
  methods: {
    /* ─────────── Récupération des détails du cabinet ─────────── */
    async fetchCabinetDetails() {
      if (!this.me?.cabinet_id) {
        console.error('Cabinet ID is missing, cannot fetch cabinet details.');
        this.cabinet_name = 'Cabinet inconnu';
        return;
      }

      try {
        const { data, error } = await supabase
          .from('cabinet')
          .select('name')
          .eq('id', this.me.cabinet_id)
          .single();

        if (error) {
          console.error('Error fetching cabinet details:', error);
          this.cabinet_name = 'Cabinet inconnu';
          return;
        }

        if (data && data.name) {
          this.cabinet_name = data.name;
        } else {
          this.cabinet_name = 'Cabinet sans nom';
        }
      } catch (e) {
        console.error('Unexpected error fetching cabinet details:', e);
        this.cabinet_name = 'Cabinet inconnu';
      }
    },

    /* ─────────── Formatage des dates ─────────── */
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /* ─────────── Graphique ─────────── */
    async fetchChart () {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7); // Corrected to go back 7 full days
      sevenDaysAgo.setHours(0, 0, 0, 0);

      if (!this.me?.cabinet_id) {
          console.error("Cabinet ID is missing, cannot fetch chart data accurately.");
          this.renderChart([], []);
          return;
      }

      const { data: profilesInCabinet, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('cabinet_id', this.me.cabinet_id);

      if (profilesError || !profilesInCabinet || profilesInCabinet.length === 0) {
          console.error("Error fetching profiles for chart or no profiles in cabinet:", profilesError);
          this.renderChart([], []);
          return;
      }

      const userIdsForChart = profilesInCabinet.map(p => p.id);

      const { data: dossiers, error: dossiersError } = await supabase
        .from('dossiers')
        .select('id, created_at, user_id')
        .in('user_id', userIdsForChart)
        .gte('created_at', sevenDaysAgo.toISOString());

      if (dossiersError) {
          console.error("Error fetching dossiers for chart:", dossiersError);
          this.renderChart([], []);
          return;
      }

      const counts = Array(7).fill(0);
      const today = new Date();
      today.setHours(0,0,0,0);

      // Adjust label generation and dossier counting to align with "last 7 days including today"
      const labels = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        labels.push(d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }));
      }

      // Ensure sevenDaysAgo is used as the start for counting dossiers for the chart
      const chartStartDate = new Date();
      chartStartDate.setDate(chartStartDate.getDate() - 6); // Data for the last 6 days + today = 7 days
      chartStartDate.setHours(0, 0, 0, 0);


      dossiers.forEach(d => {
        const dDate = new Date(d.created_at);
        // No need to setHours to 0 here if comparing against chartStartDate,
        // as gte already filters appropriately. We need to find the correct day index.
        const dayIndex = Math.floor((dDate - chartStartDate) / (24 * 60 * 60 * 1000));
        if (dayIndex >= 0 && dayIndex < 7) {
            counts[dayIndex] += 1;
        }
      });
      this.renderChart(labels, counts);
    },

    renderChart (labels, data) {
      if (!this.$refs.usageChart) return;
      const ctx = this.$refs.usageChart.getContext('2d');
      if (this.chart) this.chart.destroy();

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label  : 'Requêtes',
            data,
            fill   : false,
            tension: 0.35,
            borderWidth: 2,
            pointRadius: 3,
            borderColor: '#6c63ff',
            pointBackgroundColor: '#6c63ff'
          }]
        },
        options: {
          plugins: {
            legend: { display: false },
            tooltip: { intersect: false, mode: 'index' }
          },
          scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } },
            x: { grid: { display: false } }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    },

    /* ─────────── Tableau utilisateurs ─────────── */
    async fetchUsers () {

      if (!this.me?.cabinet_id) {
        console.error('CRITICAL: this.me.cabinet_id is missing. Cannot fetch users or their requests.');
        this.users = [];
        return;
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, email, is_cab_admin')
        .eq('cabinet_id', this.me.cabinet_id);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        this.users = [];
        return;
      }
      if (!profiles || profiles.length === 0) {
        this.users = [];
        return;
      }

      const userIdsInCabinet = profiles.map(p => p.id);
      if (userIdsInCabinet.length === 0) {
        this.users = profiles.map(p => ({ ...p, queries: 0, lastRequestDate: null }));
        return;
      }

      // Calculate the date 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0); // Set to the beginning of the day


      const { data: reqs, error: reqsError } = await supabase
        .from('dossiers')
        .select('user_id, id, created_at')
        .in('user_id', userIdsInCabinet)
        .gte('created_at', thirtyDaysAgo.toISOString()); // Filter for the last 30 days

      if (reqsError) {
        console.error('Error fetching dossiers (reqs) for the last 30 days:', reqsError);
        // Fallback to 0 queries if there's an error
        this.users = profiles.map(p => ({ ...p, queries: 0, lastRequestDate: null }));
        return;
      }


      const perUser = {};
      const lastRequestDates = {};

      if (reqs && reqs.length > 0) {
        reqs.forEach(r => {
          if (r.user_id) {
            // Count total requests per user
            perUser[r.user_id] = (perUser[r.user_id] || 0) + 1;

            // Track the latest request date for each user
            const currentDate = new Date(r.created_at);
            const existingDate = lastRequestDates[r.user_id] ? new Date(lastRequestDates[r.user_id]) : null;

            if (!existingDate || currentDate > existingDate) {
              lastRequestDates[r.user_id] = r.created_at;
            }
          }
        });
      }

      // Construire les objets utilisateurs sans tri (le tri se fait via computed)
      this.users = profiles.map(p => ({
        ...p,
        queries: perUser[p.id] || 0, // 30-day count
        lastRequestDate: lastRequestDates[p.id] || null, // Last request date
      }));

      // Le tri sera appliqué via la propriété calculée sortedUsers
    }
  }
};
</script>

<style scoped>
/* -------- conteneur principal -------- */
.admin-page.content {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem;
  font-family: 'Roboto', sans-serif;
}

/* -------- barre de titre -------- */
.title-bar {
  max-width: 1200px;
  margin: 0 auto 2rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  margin: 0;
  font-weight: 700;
  font-size: 1.8rem;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
}

/* -------- cartes -------- */
.card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  background: transparent;
  border-bottom: none;
  padding: 0 0 1rem 0;
}

.card-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.card-body {
  padding: 0;
}

/* ---- badges ---- */
.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  font-size: 0.8rem;
  font-weight: 500;
}

.badge-pill {
  padding: 4px 12px;
  border-radius: 20px;
}

.badge-light {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.badge-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.badge-info {
  background: #4dd0e1;
  color: white;
}

/* ---- tableau ---- */
.table-responsive {
  overflow-x: auto;
  border-radius: 8px;
}

.table {
  margin-bottom: 0;
  width: 100%;
  border-collapse: collapse;
}

.table th {
  border: none;
  font-weight: 600;
  color: #667eea;
  background: rgba(102, 126, 234, 0.08);
  padding: 12px 15px;
}

.table td {
  vertical-align: middle;
  padding: 12px 15px;
  border-top: 1px solid #f0f0f0;
}

.table-hover tbody tr:hover {
  background: rgba(102, 126, 234, 0.04);
}

.user-email {
  font-weight: 500;
  color: #2d3748;
}

/* ---- wrapper chart (ratio 3:1) ---- */
.chart-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 25%;
  margin: 15px 0;
}

.chart-wrapper canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 65% !important;
  height: 100% !important;
}

/* ---- classes utilitaires ---- */
.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #718096;
}

.py-4 {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.mb-5 {
  margin-bottom: 3rem;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.m-0 {
  margin: 0 !important;
}

.ml-1 {
  margin-left: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.sort-indication {
  font-size: 0.8rem;
}
</style>
